import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineArrowsRightLeft } from 'react-icons/hi2'
import { IoChevronDown } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { useCanSwitchCompany } from '../../hooks/useCanSwitchCompany'

const UserMenu = ({ light = false }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const user = useSelector((state) => state.user.user)
  const company = useSelector((state) => state.company.company)

  const { canSwitch } = useCanSwitchCompany()

  if (!user?.id) return null

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username
  const initials = (
    user.firstName && user.lastName ? `${user.firstName[0]}${user.lastName[0]}` : (fullName ?? '').slice(0, 2)
  ).toUpperCase()

  const content = (
    <>
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
          light ? 'bg-white text-[#006EAB]' : 'bg-[#073C59] text-white'
        }`}
        aria-hidden
      >
        {initials}
      </span>
      <span className={`whitespace-nowrap text-xs font-semibold ${light ? 'hidden sm:inline' : ''}`}>{fullName}</span>
      {canSwitch && <IoChevronDown className={light ? '' : 'text-gray-500'} size={14} />}
    </>
  )

  const baseClass = `flex items-center gap-3 rounded-full py-1 pl-1 pr-2 ${light ? 'text-white' : 'text-[#073C59]'}`

  if (!canSwitch) return <div className={baseClass}>{content}</div>

  return (
    // Below `sm` the menu sits mid-row in the mobile header (Layout), where a dropdown aligned to it
    // would open past the left screen edge; not positioned there, it aligns to the header instead
    <Menu as="div" className="sm:relative">
      <Menu.Button
        className={`${baseClass} duration-300 hover:cursor-pointer ${light ? 'hover:bg-white/20' : 'hover:bg-sky-100'}`}
      >
        {content}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-6 sm:right-0 z-50 w-56 mt-2 py-1 origin-top-right bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() =>
                  // companyId lets CompanySelect tell whether `from` still belongs to the active company
                  navigate('/selectcompany', {
                    state: { from: `${location.pathname}${location.search}`, companyId: company?.id },
                  })
                }
                className={`${
                  active ? 'bg-gray-50' : ''
                } w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-[#073C59]`}
              >
                <HiOutlineArrowsRightLeft className="text-[#006EAB]" size={20} />
                <span className="flex-1">{t('switch_company')}</span>
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default UserMenu
